export type StatClaimType = "industry-potential" | "survey" | "official-stat";
export type SiteLocale = "sk" | "cs" | "da" | "en" | "de";

export interface IndustryStat {
  id: string;
  value: string;
  year: number;
  sourceName: string;
  sourceUrl: string;
  claimType: StatClaimType;
  /** If set, the stat is only rendered in these locales. */
  locales?: SiteLocale[];
}

const EC_CONSTRUCTION =
  "https://single-market-economy.ec.europa.eu/sectors/raw-materials-metals-minerals-and-forest-based-industries/industries/construction_en";
const MCKINSEY_DECODE =
  "https://www.mckinsey.com/capabilities/operations/our-insights/decoding-digital-transformation-in-construction";
const MCKINSEY_PRODUCTIVITY =
  "https://www.mckinsey.com/industries/engineering-construction-and-building-materials/our-insights/delivering-on-construction-productivity-is-no-longer-optional";
const FMI_DISCONNECTED =
  "https://fmicorp.com/uploads/media/FMI_PlanGrid_Construction_Disconnected.pdf";
const AUTODESK_DATA =
  "https://www.autodesk.com/blogs/construction/harnessing-the-data-advantage-in-construction/";
const EUROSTAT_EBUSINESS =
  "https://ec.europa.eu/eurostat/databrowser/view/isoc_eb_ics/default/table?lang=en";
const EC_WASTE =
  "https://environment.ec.europa.eu/topics/waste-and-recycling/construction-and-demolition-waste_en";
const EU_OSHA =
  "https://osha.europa.eu/en/publications/digitalisation-occupational-safety-and-health";

const stats = {
  mckinseyProductivityLift: {
    id: "mckinseyProductivityLift",
    value: "14–15 %",
    year: 2024,
    sourceName: "McKinsey Global Institute",
    sourceUrl: MCKINSEY_DECODE,
    claimType: "industry-potential" as const,
  },
  mckinseyProductivityGap: {
    id: "mckinseyProductivityGap",
    value: "10 % vs 90 %",
    year: 2024,
    sourceName: "McKinsey",
    sourceUrl: MCKINSEY_PRODUCTIVITY,
    claimType: "industry-potential" as const,
  },
  ecProductivityDrop: {
    id: "ecProductivityDrop",
    value: "−8 %",
    year: 2025,
    sourceName: "European Commission",
    sourceUrl: EC_CONSTRUCTION,
    claimType: "official-stat" as const,
  },
  ecDigitalAdoption: {
    id: "ecDigitalAdoption",
    value: "55 % vs 76 %",
    year: 2025,
    sourceName: "European Commission",
    sourceUrl: EC_CONSTRUCTION,
    claimType: "official-stat" as const,
  },
  fmiSearchHours: {
    id: "fmiSearchHours",
    value: "5,5 h",
    year: 2018,
    sourceName: "FMI / PlanGrid",
    sourceUrl: FMI_DISCONNECTED,
    claimType: "survey" as const,
  },
  fmiWastedHours: {
    id: "fmiWastedHours",
    value: "14 h",
    year: 2018,
    sourceName: "FMI / PlanGrid",
    sourceUrl: FMI_DISCONNECTED,
    claimType: "survey" as const,
  },
  fmiRework: {
    id: "fmiRework",
    value: "48 %",
    year: 2018,
    sourceName: "FMI / PlanGrid",
    sourceUrl: FMI_DISCONNECTED,
    claimType: "survey" as const,
  },
  autodeskBadData: {
    id: "autodeskBadData",
    value: "40 %",
    year: 2021,
    sourceName: "Autodesk / FMI",
    sourceUrl: AUTODESK_DATA,
    claimType: "survey" as const,
  },
  autodeskBadDataDk: {
    id: "autodeskBadDataDk",
    value: "47 %",
    year: 2021,
    sourceName: "Autodesk / FMI",
    sourceUrl: AUTODESK_DATA,
    claimType: "survey" as const,
    locales: ["da"] as SiteLocale[],
  },
  autodeskPmTime: {
    id: "autodeskPmTime",
    value: "49 %",
    year: 2021,
    sourceName: "Autodesk / FMI",
    sourceUrl: AUTODESK_DATA,
    claimType: "survey" as const,
  },
  autodeskDataDecisions: {
    id: "autodeskDataDecisions",
    value: "9 %",
    year: 2021,
    sourceName: "Autodesk / FMI",
    sourceUrl: AUTODESK_DATA,
    claimType: "survey" as const,
  },
  eurostatErpGap: {
    id: "eurostatErpGap",
    value: "41 % vs 89 %",
    year: 2025,
    sourceName: "Eurostat",
    sourceUrl: EUROSTAT_EBUSINESS,
    claimType: "official-stat" as const,
  },
  eurostatCrm: {
    id: "eurostatCrm",
    value: "16,43 %",
    year: 2025,
    sourceName: "Eurostat",
    sourceUrl: EUROSTAT_EBUSINESS,
    claimType: "official-stat" as const,
  },
  eurostatBi: {
    id: "eurostatBi",
    value: "6,77 %",
    year: 2025,
    sourceName: "Eurostat",
    sourceUrl: EUROSTAT_EBUSINESS,
    claimType: "official-stat" as const,
  },
  ecWasteShare: {
    id: "ecWasteShare",
    value: ">⅓",
    year: 2024,
    sourceName: "European Commission",
    sourceUrl: EC_WASTE,
    claimType: "official-stat" as const,
  },
  euOshaDigitalSafety: {
    id: "euOshaDigitalSafety",
    value: "EU-OSHA",
    year: 2025,
    sourceName: "EU-OSHA",
    sourceUrl: EU_OSHA,
    claimType: "official-stat" as const,
  },
} satisfies Record<string, IndustryStat>;

export type IndustryStatId = keyof typeof stats;

export const INDUSTRY_STATS: Record<IndustryStatId, IndustryStat> = stats;

export const HOMEPAGE_STRIP_IDS: IndustryStatId[] = [
  "mckinseyProductivityLift",
  "ecDigitalAdoption",
  "eurostatErpGap",
  "fmiSearchHours",
];

export const LANDING_STRIP_IDS: Record<string, IndustryStatId[]> = {
  "sprava-dokumentov": ["fmiSearchHours", "fmiRework", "autodeskBadData", "autodeskBadDataDk"],
  "projektove-riadenie": ["mckinseyProductivityGap", "autodeskBadData", "autodeskPmTime", "autodeskBadDataDk"],
  "reporting-analytika": ["autodeskDataDecisions", "autodeskBadData", "eurostatBi", "autodeskBadDataDk"],
  "crm-system-prehlad-zakaznikov": ["eurostatCrm", "eurostatErpGap", "autodeskDataDecisions"],
  "sklad-materialy": ["ecWasteShare", "autodeskBadData"],
  "sledovanie-materialov": ["ecWasteShare", "fmiSearchHours"],
  "vykaz-prace-a-evidencia-dochadzky": ["fmiWastedHours", "fmiSearchHours", "autodeskPmTime"],
  "bezpecnost-kvalita": ["euOshaDigitalSafety", "fmiRework"],
  "majetok-firmy": ["fmiSearchHours", "autodeskBadData"],
};

export const SOCIAL_PROOF_STAT_IDS: IndustryStatId[] = [
  "ecDigitalAdoption",
  "mckinseyProductivityLift",
  "eurostatErpGap",
];

export function isStatVisible(stat: IndustryStat, locale: string): boolean {
  if (!stat.locales || stat.locales.length === 0) return true;
  return stat.locales.includes(locale as SiteLocale);
}

export function statsForContext(context: string, locale: string): IndustryStat[] {
  const ids = context === "home" ? HOMEPAGE_STRIP_IDS : LANDING_STRIP_IDS[context] || [];
  return ids
    .map((id) => INDUSTRY_STATS[id])
    .filter((stat) => isStatVisible(stat, locale));
}
